<template>
    <!-- Loading State - Sadece loading varken göster -->
    <div v-if="loading" class="loading-overlay">
        <LoadingSpinner />
    </div>
    
    <!-- Form Content - Loading yokken göster -->
    <div v-else class="form-wrapper">
        <div v-for="logo in getFormLogo()" :key="logo._id" class="logo-container">
            <img
                :src="logo.cdnUrl || `https://static.supsis.live/${logo.path + logo._id}.${logo.ext}`"
                :alt="logo.name"
                :style="{ width: logo.width + 'px', height: logo.height + 'px'}"
            />
        </div>
        <div :class="['container', { 'special-background': isSpecial }]">
            <div v-if="error">{{ error }}</div>
            <div v-if="sortedFields.length && !submissionSuccess">
                <div v-if="isPaginated" class="paginated-form">
                    <div class="form-page-container">
                        <transition name="page-slide" mode="out-in">
                            <div :key="currentPage" class="form-page">
                                <component
                                    v-for="field in getCurrentPageFields()"
                                    :key="field._id"
                                    :is="getComponent(field.type)"
                                    v-bind="getPageBreakProps(field)"
                                    v-model="formValues[field._id]"
                                    :showError="showError"
                                    :formId="formId"
                                    :order="field.order"
                                    :fieldId="field._id"
                                    v-on="getPageBreakEvents(field)"
                                    @validate="handleValidation"
                                />
                            </div>
                        </transition>
                    </div>
                </div>
                
                <div v-else class="regular-form">
                    <component
                        v-for="field in sortedFields"
                        :key="field._id"
                        :is="getComponent(field.type)"
                        v-bind="getProps(field)"
                        v-model="formValues[field._id]"
                        :showError="showError"
                        :formId="formId"
                        :order="field.order"
                        :fieldId="field._id"
                        v-on="field.type === 'send' ? { submit: submitForm } : {}"
                        @validate="handleValidation"
                    />
                </div>
            </div>
            <div v-if="submissionSuccess">
                <SubmitForm />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Title from "./item/Title.vue";
import FullName from "./item/FullName.vue";
import Email from "./item/Email.vue";
import Address from "./item/Address.vue";
import PhoneNumber from "./item/PhoneNumber.vue";
import DatePicker from "./item/DatePicker.vue";
import ShortText from "./item/ShortText.vue";
import LongText from "./item/LongText.vue";
import Number from "./item/Number.vue";
import Hour from "./item/Hour.vue";
import Spinner from "./item/Spinner.vue";
import SendBtn from "./item/SendBtn.vue";
import StarRating from "./item/StarRating.vue";
import RatingScale from "./item/RatingScale.vue";
import SeparationElement from "./item/SeparationElement.vue";
import SubmitForm from "./SubmitForm.vue";
import LoadingSpinner from "./LoadingSpinner.vue";
import FileUpload from "./item/FileUpload.vue";
import DropDownList from "./item/DropDownList.vue";
import SingleChoice from "./item/SingleChoice.vue";
import MultipleSelection from "./item/MultipleSelection.vue";
import FormImage from "./item/FormImage.vue";
import Paragraph from "./item/Paragraph.vue";
import Signature from "./item/Signature.vue";
import Captcha from "./item/Captcha.vue";
import PageBreak from "./item/PageBreak.vue";

function generateRandomId() {
    return "xxxxxxx".replace(/x/g, function () {
        const r = (Math.random() * 16) | 0;
        return r.toString(16);
    });
}

export default {
    name: "FormContainer",
    components: {
        Title,
        FullName,
        Email,
        Address,
        PhoneNumber,
        DatePicker,
        ShortText,
        LongText,
        Number,
        Hour,
        Spinner,
        SendBtn,
        StarRating,
        RatingScale,
        SeparationElement,
        SubmitForm,
        LoadingSpinner,
        FileUpload,
        DropDownList,
        SingleChoice,
        MultipleSelection,
        FormImage,
        Paragraph,
        Signature,
        Captcha,
        PageBreak,
    },
    data() {
        return {
            formId: this.$route.params.formId,
            siteId: this.$route.params.siteId,
            formValues: {},
            userDeviceId: null,
            hasSendButton: false,
            validationErrors: {},
            showError: false,
            queryUsername: null,
            queryFullName: null,
            queryParams: {},
            isSpecial: false,
            submissionTriggered: false,
            isFormReady: false,
            currentPage: 0,
            totalPages: 0,
            formPages: [],
            isPaginated: false,
        };
    },
    created() {
        const queryString = this.$route.fullPath.split("?")[1];
        const queryParams = new URLSearchParams(queryString);
        this.queryUsername = queryParams.get("username");
        this.queryFullName = queryParams.get("fullName");

        this.queryParams = {};
        if (queryParams) {
            for (let [key, value] of queryParams) {
                this.queryParams[key] = value;
            }
        }


        let userDeviceId = this.getCookie("userDeviceId");
        if (!userDeviceId) {
            userDeviceId = generateRandomId();
            this.setCookie("userDeviceId", userDeviceId, 365);
        }
        this.userDeviceId = userDeviceId;
        this.fetchFormData({ siteId: this.siteId, formId: this.formId }).then((res) => {
            if (!res) {
                this.$router.push({ name: "NotFound" });
            }
        });
    },
    computed: {
        ...mapGetters("form", ["formData", "loading", "error", "submissionSuccess"]),
        sortedFields() {
            if (this.formData && this.formData.formFields) {
                this.checkForSendButton(this.formData.formFields);
                return [...this.formData.formFields]
                    .filter(field => field.visibility !== false)
                    .sort((a, b) => a.order - b.order);
            }
            return [];
        },
    },
    watch: {
        formData: {
            handler(newFormData) {
                if (newFormData && newFormData.formFields) {
                    console.log("🔍 FormData Watch - Query Params:", this.queryParams);
                    console.log("🔍 FormData Watch - Form Fields:", newFormData.formFields.map(f => ({
                        id: f._id,
                        type: f.type,
                        slug: f.slug,
                        label: f.label,
                    })));
                    
                    newFormData.formFields.forEach((field) => {
                        let fieldValue = field.value;

                        console.log(`🔍 Processing field: ${field.label} (type: ${field.type}, slug: ${field.slug})`);

                        if (field.slug && this.queryParams && this.queryParams[field.slug]) {
                            const slugValue = this.queryParams[field.slug];
                            console.log(`✅ Slug '${field.slug}' found with value '${slugValue}' for field type '${field.type}'`);
                            console.log(`🔄 Original field value:`, field.value);
                            console.log(`🔄 Query value to apply:`, slugValue);

                            switch (field.type) {
                                case "fullName":
                                    fieldValue = {
                                        firstName: slugValue,
                                        lastName: "",
                                    };
                                    break;
                                case "email":
                                    fieldValue = {
                                        email: slugValue,
                                    };
                                    break;
                                case "shortText":
                                    fieldValue = {
                                        shortText: slugValue,
                                    };
                                    break;
                                case "longText":
                                    fieldValue = {
                                        longText: slugValue,
                                    };
                                    break;
                                case "number":
                                    fieldValue = {
                                        number: parseInt(slugValue) || 0,
                                    };
                                    break;
                                case "phone":
                                    fieldValue = {
                                        phoneNumber: slugValue,
                                    };
                                    break;
                                case "address":
                                    fieldValue = {
                                        fieldAddress: slugValue,
                                        addressLine1: slugValue,
                                        addressLine2: "",
                                        city: "",
                                        state: "",
                                        postalCode: "",
                                    };
                                    break;
                                case "datePicker":
                                    fieldValue = {
                                        date: slugValue,
                                    };
                                    break;
                                case "hour":
                                    fieldValue = {
                                        time: slugValue,
                                        ampm: "AM",
                                    };
                                    break;
                                case "spinner":
                                    fieldValue = {
                                        spinnerValue: parseInt(slugValue) || 0,
                                    };
                                    break;
                                case "starRating":
                                    fieldValue = {
                                        rating: parseInt(slugValue) || 0,
                                    };
                                    break;
                                case "ratingScale":
                                    fieldValue = {
                                        rating: parseInt(slugValue) || 0,
                                    };
                                    break;
                                case "dropDownList":
                                    fieldValue = {
                                        selected: slugValue,
                                    };
                                    break;
                                case "singleChoice":
                                    fieldValue = slugValue;
                                    break;
                                case "multipleSelection":
                                    fieldValue = [slugValue];
                                    break;
                                default:
                                    fieldValue = slugValue;
                                    break;
                            }
                            console.log(`✅ New field value after slug processing:`, fieldValue);
                        } else if (field.slug) {
                            console.log(`❌ Field has slug '${field.slug}' but no matching query param found`);
                        }

                        if (field.type === 'fileUpload') {
                            this.$set(this.formValues, field._id, {
                                ...this.formValues[field._id],
                                fieldId: field._id,
                                values: this.formValues[field._id]?.values || []
                            });
                        } else if (field.type === 'multipleSelection') {
                            this.$set(this.formValues, field._id, fieldValue || []);
                        } else {
                            this.$set(this.formValues, field._id, fieldValue || "");
                        }

                        console.log(`🔧 Final formValues[${field._id}] =`, this.formValues[field._id]);
                    });
                    
                    this.$nextTick(() => {
                        this.initializePagination();
                    });
                }
            },
            immediate: true,
            deep: true,
        },
        loading(isLoading) {
            if (isLoading) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    },
    methods: {
        ...mapActions("form", ["fetchFormData", "submitFormData"]),
        
        initializePagination() {
            const pageBreakFields = this.sortedFields.filter(field => field.type === 'pagebreak');
            
            console.log('🔴 initializePagination called:', {
                sortedFieldsLength: this.sortedFields.length,
                pageBreakFieldsLength: pageBreakFields.length,
                pageBreakFields: pageBreakFields.map(f => ({ id: f._id, type: f.type, order: f.order }))
            });
            
            if (pageBreakFields.length === 0) {
                this.isPaginated = false;
                console.log('🔴 No PageBreak fields found, using regular form');
                return;
            }
            
            this.isPaginated = true;
            this.formPages = [];
            
            let pageBreakIndices = [];
            pageBreakFields.forEach(pageBreakField => {
                const index = this.sortedFields.findIndex(f => f._id === pageBreakField._id);
                if (index !== -1) {
                    pageBreakIndices.push(index);
                }
            });
            
            console.log('🔴 PageBreak indices found:', pageBreakIndices);
            
            let currentPageStart = 0;
            
            pageBreakIndices.forEach((pageBreakIndex, i) => {
                const pageFields = this.sortedFields.slice(currentPageStart, pageBreakIndex + 1);
                this.formPages.push(pageFields);
                console.log(`🔴 Page ${i + 1} created with ${pageFields.length} fields:`, pageFields.map(f => f.type));
                
                currentPageStart = pageBreakIndex + 1;
            });
            
            if (currentPageStart < this.sortedFields.length) {
                const lastPageFields = this.sortedFields.slice(currentPageStart);
                this.formPages.push(lastPageFields);
                console.log(`🔴 Last page created with ${lastPageFields.length} fields:`, lastPageFields.map(f => f.type));
            }
            
            this.totalPages = this.formPages.length;
            this.currentPage = 0;
            
            console.log('🔴 Pagination initialized:', {
                totalPages: this.totalPages,
                currentPage: this.currentPage,
                isPaginated: this.isPaginated,
                formPages: this.formPages.map((page, i) => ({
                    pageIndex: i,
                    fieldsCount: page.length,
                    fieldTypes: page.map(f => f.type)
                }))
            });
            
            this.logPaginationStatus();
        },
        
        /**
         * Güvenli string trim helper - value'nun tipini kontrol eder
         * @param {any} value - Kontrol edilecek değer
         * @param {string} fieldType - Field tipi (shortText, longText vs.)
         * @returns {boolean} - Boş mu değil mi
         */
        isSafeEmpty(value, fieldType = null) {
            if (!value) return true;
            
            // Field type'a göre özel kontroller
            if (fieldType === 'shortText') {
                // shortText için value.shortText kontrol et
                const textValue = value.shortText;
                if (!textValue) return true;
                if (typeof textValue === 'string') return textValue.trim() === '';
                if (Array.isArray(textValue)) return textValue.length === 0 || !textValue[0] || textValue[0].trim() === '';
                return false;
            }
            
            if (fieldType === 'longText') {
                // longText için value.longText kontrol et
                const textValue = value.longText;
                if (!textValue) return true;
                if (typeof textValue === 'string') return textValue.trim() === '';
                if (Array.isArray(textValue)) return textValue.length === 0 || !textValue[0] || textValue[0].trim() === '';
                return false;
            }
            
            // String ise
            if (typeof value === 'string') {
                return value.trim() === '';
            }
            
            // Array ise (form değerleri bazen array olarak geliyor)
            if (Array.isArray(value)) {
                return value.length === 0 || !value[0] || 
                       (typeof value[0] === 'string' && value[0].trim() === '');
            }
            
            // Object ise (nested değerler için)
            if (typeof value === 'object') {
                // Object'in tüm string değerleri boş mu kontrol et
                const stringValues = Object.values(value).filter(v => typeof v === 'string');
                return stringValues.length === 0 || stringValues.every(v => v.trim() === '');
            }
            
            // Diğer tipler için false (number, boolean vs.)
            return false;
        },
        
        logPaginationStatus() {
            console.log('📊 PAGINATION STATUS:', {
                isPaginated: this.isPaginated,
                currentPage: this.currentPage,
                totalPages: this.totalPages,
                formPagesLength: this.formPages.length,
                sortedFieldsCount: this.sortedFields.length,
                pageBreakCount: this.sortedFields.filter(f => f.type === 'pagebreak').length
            });
        },
        
        getCurrentPageFields() {
            if (!this.isPaginated || this.formPages.length === 0) {
                console.log('🔴 getCurrentPageFields: Not paginated or no pages');
                return [];
            }
            
            const currentFields = this.formPages[this.currentPage] || [];
            console.log('🔴 getCurrentPageFields:', {
                currentPage: this.currentPage,
                totalPages: this.totalPages,
                currentFieldsCount: currentFields.length,
                currentFieldTypes: currentFields.map(f => f.type)
            });
            
            return currentFields;
        },
        
        goToNextPage() {
            if (this.currentPage < this.totalPages - 1) {
                if (this.validateCurrentPage()) {
                    this.currentPage++;
                    console.log('🔴 Next page:', this.currentPage);
                    this.showError = false;
                } else {
                    console.log('🔴 Page validation failed, staying on current page');
                    this.showError = true;
                }
            }
        },
        
        validateCurrentPage() {
            const currentFields = this.getCurrentPageFields();
            
            const fieldsToValidate = currentFields.filter(field => field.type !== 'pagebreak');
            
            console.log('🔴 Validating current page fields:', fieldsToValidate.map(f => ({ 
                type: f.type, 
                required: this.isFieldRequired(f), 
                value: this.formValues[f._id] 
            })));
            
            const visibleFieldIds = fieldsToValidate.map(field => field._id);
            const visibleFieldErrors = Object.entries(this.validationErrors)
                .filter(([fieldId]) => visibleFieldIds.includes(fieldId))
                .map(([, hasError]) => hasError);

            const requiredFieldsEmpty = fieldsToValidate.filter(field => {
                if (field.type === 'fullName' && field.fullName?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.firstName || !value.lastName || 
                                   value.firstName.trim() === '' || value.lastName.trim() === '';
                    console.log(`🔍 PageBreak Required FullName kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.fullName.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'email' && field.email?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.email || value.email.trim() === '';
                    console.log(`🔍 PageBreak Required Email kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.email.fieldEmail,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'address' && field.address?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.addressLine1 || !value.addressLine2 || 
                                   !value.city || !value.state || !value.postalCode ||
                                   value.addressLine1.trim() === '' || value.addressLine2.trim() === '' ||
                                   value.city.trim() === '' || value.state.trim() === '' || 
                                   value.postalCode.trim() === '';
                    console.log(`🔍 PageBreak Required Address kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.address.fieldAddress,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'phone' && field.phone?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.phoneNumber || value.phoneNumber.trim() === '';
                    console.log(`🔍 PageBreak Required Phone kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.phone.fieldPhone,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'datePicker' && field.datePicker?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.date || value.date.trim() === '';
                    console.log(`🔍 PageBreak Required DatePicker kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.datePicker.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'shortText' && field.shortText?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = this.isSafeEmpty(value);
                    console.log(`🔍 PageBreak Required ShortText kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.shortText.fieldLabel,
                        value, 
                        valueType: typeof value,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'longText' && field.longText?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = this.isSafeEmpty(value);
                    console.log(`🔍 PageBreak Required LongText kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.longText.fieldLabel,
                        value, 
                        valueType: typeof value,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'singleChoice' && field.singleChoice?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || value === '';
                    console.log(`🔍 PageBreak Required SingleChoice kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.singleChoice.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'multipleSelection' && field.multipleSelection?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !Array.isArray(value) || value.length === 0;
                    console.log(`🔍 PageBreak Required MultipleSelection kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.multipleSelection.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'dropDownList' && field.dropDownList?.isRequired) {
                    const value = this.formValues[field._id];
                    const selectedValue = value?.selected;
                    const isEmpty = !selectedValue || 
                                   (typeof selectedValue === 'string' && selectedValue.trim() === '') ||
                                   (Array.isArray(selectedValue) && (selectedValue.length === 0 || !selectedValue[0]));
                    console.log(`🔍 PageBreak Required DropDownList kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.dropDownList.fieldLabel,
                        value, 
                        selectedValue,
                        selectedValueType: typeof selectedValue,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'signature' && field.signature?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || value === '';
                    console.log(`🔍 PageBreak Required Signature kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.signature.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'number' && field.number?.isRequired) {
                    const value = this.formValues[field._id];
                    const numberValue = value?.number;
                    const isEmpty = numberValue === null || numberValue === undefined || numberValue === '';
                    console.log(`🔍 PageBreak Required Number kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.number.fieldLabel,
                        value, 
                        numberValue,
                        numberValueType: typeof numberValue,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'hour' && field.hour?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.time || value.time.trim() === '';
                    console.log(`🔍 PageBreak Required Hour kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.hour.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'spinner' && field.spinner?.isRequired) {
                    const value = this.formValues[field._id];
                    const spinnerValue = value?.spinnerValue;
                    const isEmpty = spinnerValue === null || spinnerValue === undefined || spinnerValue === '';
                    console.log(`🔍 PageBreak Required Spinner kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.spinner.fieldLabel,
                        value, 
                        spinnerValue,
                        spinnerValueType: typeof spinnerValue,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'captcha' && field.captcha?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || value === '';
                    console.log(`🔍 PageBreak Required Captcha kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.captcha.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                return false;
            });

            const hasErrors = visibleFieldErrors.some((error) => error) || requiredFieldsEmpty.length > 0;
            
            console.log('🔍 PageBreak Validation kontrol:', {
                visibleFieldIds,
                validationErrors: this.validationErrors,
                visibleFieldErrors,
                requiredFieldsEmpty: requiredFieldsEmpty.map(f => ({ 
                    id: f._id, 
                    type: f.type,
                    label: this.getFieldLabel(f)
                })),
                hasErrors
            });

            if (hasErrors) {
                console.log('🔴 PageBreak validation failed - showing individual field errors');
                return false;
            }
            
            console.log('🔴 PageBreak validation passed');
            return true;
        },
        
        getFieldLabel(field) {
            switch (field.type) {
                case 'fullName': return field.fullName?.fieldLabel;
                case 'email': return field.email?.fieldEmail;
                case 'address': return field.address?.fieldAddress;
                case 'phone': return field.phone?.fieldPhone;
                case 'datePicker': return field.datePicker?.fieldLabel;
                case 'shortText': return field.shortText?.fieldLabel;
                case 'longText': return field.longText?.fieldLabel;
                case 'number': return field.number?.fieldLabel;
                case 'hour': return field.hour?.fieldLabel;
                case 'spinner': return field.spinner?.fieldLabel;
                case 'singleChoice': return field.singleChoice?.fieldLabel;
                case 'multipleSelection': return field.multipleSelection?.fieldLabel;
                case 'dropDownList': return field.dropDownList?.fieldLabel;
                case 'signature': return field.signature?.fieldLabel;
                case 'captcha': return field.captcha?.fieldLabel;
                default: return field.label;
            }
        },
        
        isFieldRequired(field) {
            switch (field.type) {
                case 'fullName': return field.fullName?.isRequired;
                case 'email': return field.email?.isRequired;
                case 'address': return field.address?.isRequired;
                case 'phone': return field.phone?.isRequired;
                case 'datePicker': return field.datePicker?.isRequired;
                case 'shortText': return field.shortText?.isRequired;
                case 'longText': return field.longText?.isRequired;
                case 'number': return field.number?.isRequired;
                case 'hour': return field.hour?.isRequired;
                case 'spinner': return field.spinner?.isRequired;
                case 'singleChoice': return field.singleChoice?.isRequired;
                case 'multipleSelection': return field.multipleSelection?.isRequired;
                case 'dropDownList': return field.dropDownList?.isRequired;
                case 'signature': return field.signature?.isRequired;
                case 'captcha': return field.captcha?.isRequired;
                default: return field.required;
            }
        },
        
        
        goToPreviousPage() {
            if (this.currentPage > 0) {
                this.currentPage--;
                console.log('🔴 Previous page:', this.currentPage);
                this.showError = false;
            }
        },
        
        getPageBreakProps(field) {
            if (field.type === 'pagebreak') {
                return {
                    ...this.getProps(field),
                    isFirstPage: this.currentPage === 0,
                    isLastPage: this.currentPage === this.totalPages - 1,
                    currentPage: this.currentPage + 1,
                    totalPages: this.totalPages
                };
            }
            return this.getProps(field);
        },
        
        getPageBreakEvents(field) {
            if (field.type === 'pagebreak') {
                return {
                    'next-page': this.goToNextPage,
                    'previous-page': this.goToPreviousPage
                };
            }
            if (field.type === 'send') {
                return { submit: this.submitForm };
            }
            return {};
        },
        
        getFormLogo() {
            return this.formData?.form?.logo || [];
        },
        getComponent(type) {
            switch (type) {
                case "title":
                    return "Title";
                case "fullName":
                    return "FullName";
                case "email":
                    return "Email";
                case "address":
                    return "Address";
                case "phone":
                    return "PhoneNumber";
                case "datePicker":
                    return "DatePicker";
                case "shortText":
                    return "ShortText";
                case "longText":
                    return "LongText";
                case "number":
                    return "Number";
                case "hour":
                    return "Hour";
                case "spinner":
                    return "Spinner";
                case "send":
                    return "SendBtn";
                case "starRating":
                    return "StarRating";
                case "ratingScale":
                    return "RatingScale";
                case "separationElement":
                    return "SeparationElement";
                case "fileUpload":
                    return "FileUpload";
                case "dropDownList":
                    return "DropDownList";
                case "singleChoice":
                    return "SingleChoice";
                case "multipleSelection":
                    return "MultipleSelection";
                case "image":
                    return "FormImage";
                case "paragraph":
                    return "Paragraph";
                case "signature":
                    return "Signature";
                case "captcha":
                    return "Captcha";
                case "pagebreak":
                    return "PageBreak";
                default:
                    return null;
            }
        },
        getProps(field) {
            switch (field.type) {
                case "title":
                    return { ...field.title };
                case "fullName":
                    return { ...field.fullName };
                case "email":
                    return { ...field.email };
                case "address":
                    return { ...field.address };
                case "phone":
                    return { ...field.phone };
                case "datePicker":
                    return { ...field.datePicker };
                case "shortText":
                    return { ...field.shortText };
                case "longText":
                    return { ...field.longText };
                case "number":
                    return { ...field.number };
                case "hour":
                    return { ...field.hour };
                case "spinner":
                    return { ...field.spinner };
                case "send":
                    return { ...field.send };
                case "starRating":
                    return { ...field.starRating };
                case "ratingScale":
                    return { ...field.ratingScale };
                case "separationElement":
                    return { ...field.separationElement };
                case "fileUpload":
                    return { 
                        ...field.fileUpload, 
                        siteId: this.siteId,
                        fieldId: field._id
                    };
                case "dropDownList":
                    return { ...field.dropDownList, siteId: this.siteId };
                case "singleChoice":
                    return { 
                        ...field.singleChoice, 
                        siteId: this.siteId,
                        defaultValues: (field.singleChoice.defaultValues && Array.isArray(field.singleChoice.defaultValues)) ? field.singleChoice.defaultValues : []
                    };
                case "multipleSelection":
                    return {
                        ...field.multipleSelection,
                        siteId: this.siteId,
                        defaultValues: (field.multipleSelection.defaultValues && Array.isArray(field.multipleSelection.defaultValues)) ? field.multipleSelection.defaultValues : [],
                        value: Array.isArray(this.formValues[field._id])
                            ? this.formValues[field._id]
                            : this.formValues[field._id]
                                ? [this.formValues[field._id]]
                                : []
                    };
                case "image":
                    return { ...field.image , siteId: this.siteId };
                case "paragraph":
                    return { ...field.paragraph };
                case "signature":
                    return { 
                        ...field.signature,
                        formId: this.formId,
                        order: field.order,
                        shouldShowError: this.shouldShowSignatureError(field),
                        isSpecial: this.isSpecial
                    };
                case "captcha":
                    return { 
                        ...field.captcha,
                        formId: this.formId,
                        order: field.order,
                        shouldShowError: this.shouldShowCaptchaError(field),
                        isSpecial: this.isSpecial
                    };
                case "pagebreak":
                    return {
                        ...field.pagebreak,
                        formId: this.formId,
                        order: field.order,
                        isSpecial: this.isSpecial
                    };
                default:
                    return {};
            }
        },
        getCookie(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(";").shift();
        },
        setCookie(name, value, days) {
            const d = new Date();
            d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
            const expires = `expires=${d.toUTCString()}`;
            document.cookie = `${name}=${value};${expires};path=/`;
        },
        checkForSendButton(fields) {
            this.hasSendButton = fields.some((field) => field.type === "send");
        },
        shouldShowSignatureError(field) {
            // Signature için error kontrolü
            if (!this.showError || !field.signature?.isRequired) {
                return false;
            }
            
            const value = this.formValues[field._id];
            const isEmpty = !value || !value.value || value.value.trim() === '';
            
            console.log('🔍 Signature Error Check:', {
                fieldId: field._id,
                fieldLabel: field.signature?.fieldLabel,
                isRequired: field.signature?.isRequired,
                value,
                isEmpty,
                showError: this.showError
            });
            
            return isEmpty;
        },
        shouldShowCaptchaError(field) {
            if (!this.showError || !field.captcha?.isRequired) {
                return false;
            }
            
            const value = this.formValues[field._id];
            const isEmpty = !value || !value.captchaInput || !value.isValid;
            
            console.log('🔍 Captcha Error Check:', {
                fieldId: field._id,
                fieldLabel: field.captcha?.fieldLabel,
                isRequired: field.captcha?.isRequired,
                value,
                isEmpty,
                showError: this.showError
            });
            
            return isEmpty;
        },
        handleValidation({ fieldId, hasError }) {
            console.log('🔍 Validation received:', { fieldId, hasError });
            this.$set(this.validationErrors, fieldId, hasError);
            console.log('🔍 Updated validationErrors:', this.validationErrors);
        },
        async submitForm(eventData) {
            console.log('🔴 FormContainer submit triggered:', {
                eventData,
                hasSendButton: this.hasSendButton,
                submissionTriggered: this.submissionTriggered,
                timestamp: new Date().toISOString(),
                callStack: new Error().stack
            });

            // SendBtn kontrolü - sadece send butonu varsa ve tıklandığında gönder
            if (!this.hasSendButton) {
                console.log('❌ Form gönderimi iptal edildi: Send butonu bulunamadı');
                return;
            }

            // Explicit submit kontrolü - sadece SendBtn'den gelen submit'leri kabul et
            if (!eventData || !eventData.triggeredByButton) {
                console.log('❌ Form gönderimi iptal edildi: Submit butonu dışında tetiklendi', eventData);
                return;
            }

            if (!eventData.userClick) {
                console.log('❌ Form gönderimi iptal edildi: User click bulunamadı');
                return;
            }

            if (this.submissionTriggered) {
                console.log('❌ Form gönderimi iptal edildi: Zaten gönderilmiş');
                return;
            }

            const isInIframe = window !== window.top;
            if (isInIframe && !this.isFormReady) {
                console.log('❌ Form gönderimi iptal edildi: iframe henüz hazır değil');
                return;
            }

            // Önce tüm field'lar için validation durumunu güncelle
            this.sortedFields.forEach(field => {
                if (field.type === 'fullName' && field.fullName?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.firstName || !value.lastName || 
                                   value.firstName.trim() === '' || value.lastName.trim() === '';
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'email' && field.email?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.email || value.email.trim() === '';
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'shortText' && field.shortText?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = this.isSafeEmpty(value, 'shortText');
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'longText' && field.longText?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = this.isSafeEmpty(value, 'longText');
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'number' && field.number?.isRequired) {
                    const value = this.formValues[field._id];
                    const numberValue = value?.number;
                    const isEmpty = numberValue === null || numberValue === undefined || numberValue === '';
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'hour' && field.hour?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.time || value.time.trim() === '';
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'spinner' && field.spinner?.isRequired) {
                    const value = this.formValues[field._id];
                    const spinnerValue = value?.spinnerValue;
                    const isEmpty = spinnerValue === null || spinnerValue === undefined || spinnerValue === '';
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'dropDownList' && field.dropDownList?.isRequired) {
                    const value = this.formValues[field._id];
                    const selectedValue = value?.selected;
                    const isEmpty = !selectedValue || 
                                   (typeof selectedValue === 'string' && selectedValue.trim() === '') ||
                                   (Array.isArray(selectedValue) && (selectedValue.length === 0 || !selectedValue[0]));
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'singleChoice' && field.singleChoice?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || value === '';
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'multipleSelection' && field.multipleSelection?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !Array.isArray(value) || value.length === 0;
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'fileUpload' && field.fileUpload?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.values || !Array.isArray(value.values) || value.values.length === 0;
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'signature' && field.signature?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.value || value.value.trim() === '';
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
                if (field.type === 'captcha' && field.captcha?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.captchaInput || !value.isValid;
                    this.$set(this.validationErrors, field._id, isEmpty);
                }
            });

            const visibleFieldIds = this.sortedFields.map(field => field._id);
            
            console.log('🔍 DETAILED validation state after update:', {
                allValidationErrors: this.validationErrors,
                visibleFieldIds: visibleFieldIds,
                validationErrorEntries: Object.entries(this.validationErrors)
            });
            
            const visibleFieldErrors = Object.entries(this.validationErrors)
                .filter(([fieldId]) => visibleFieldIds.includes(fieldId))
                .map(([, hasError]) => hasError);

            console.log('🔍 visibleFieldErrors after filtering:', visibleFieldErrors);

            const requiredFieldsEmpty = this.sortedFields.filter(field => {
                if (field.type === 'fullName' && field.fullName?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.firstName || !value.lastName || 
                                   value.firstName.trim() === '' || value.lastName.trim() === '';
                    console.log(`🔍 Required FullName kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.fullName.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'email' && field.email?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.email || value.email.trim() === '';
                    console.log(`🔍 Required Email kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.email.fieldEmail,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'address' && field.address?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.addressLine1 || !value.addressLine2 || 
                                   !value.city || !value.state || !value.postalCode ||
                                   value.addressLine1.trim() === '' || value.addressLine2.trim() === '' ||
                                   value.city.trim() === '' || value.state.trim() === '' || 
                                   value.postalCode.trim() === '';
                    console.log(`🔍 Required Address kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.address.fieldAddress,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'phone' && field.phone?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.phoneNumber || value.phoneNumber.trim() === '';
                    console.log(`🔍 Required Phone kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.phone.fieldPhone,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'datePicker' && field.datePicker?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.date || value.date.trim() === '';
                    console.log(`🔍 Required DatePicker kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.datePicker.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'shortText' && field.shortText?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = this.isSafeEmpty(value, 'shortText');
                    console.log(`🔍 Required ShortText kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.shortText.fieldLabel,
                        value, 
                        valueType: typeof value,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'longText' && field.longText?.isRequired) {
                    const value = this.formValues[field._id];
                    const textValue = value?.longText;
                    const isEmpty = !textValue || 
                                   (typeof textValue === 'string' && textValue.trim() === '') || 
                                   (Array.isArray(textValue) && (textValue.length === 0 || !textValue[0] || textValue[0].trim() === ''));
                    console.log(`🔍 Required LongText kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.longText.fieldLabel,
                        value, 
                        textValue,
                        textValueType: typeof textValue,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'number' && field.number?.isRequired) {
                    const value = this.formValues[field._id];
                    const numberValue = value?.number;
                    const isEmpty = numberValue === null || numberValue === undefined || numberValue === '';
                    console.log(`🔍 Required Number kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.number.fieldLabel,
                        value, 
                        numberValue,
                        numberValueType: typeof numberValue,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'spinner' && field.spinner?.isRequired) {
                    const value = this.formValues[field._id];
                    const spinnerValue = value?.spinnerValue;
                    const isEmpty = spinnerValue === null || spinnerValue === undefined || spinnerValue === '';
                    console.log(`🔍 Required Spinner kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.spinner.fieldLabel,
                        value, 
                        spinnerValue,
                        spinnerValueType: typeof spinnerValue,
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'dropDownList' && field.dropDownList?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.selected || value.selected.trim() === '';
                    console.log(`🔍 Required DropDownList kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.dropDownList.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'singleChoice' && field.singleChoice?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || value === '';
                    console.log(`🔍 Required SingleChoice kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.singleChoice.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'multipleSelection' && field.multipleSelection?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !Array.isArray(value) || value.length === 0;
                    console.log(`🔍 Required MultipleSelection kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.multipleSelection.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'fileUpload' && field.fileUpload?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.values || !Array.isArray(value.values) || value.values.length === 0;
                    console.log(`🔍 Required FileUpload kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.fileUpload.fieldLabelFile,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'hour' && field.hour?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.time || value.time.trim() === '';
                    console.log(`🔍 Required Hour kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.hour.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'signature' && field.signature?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.value || value.value.trim() === '';
                    console.log(`🔍 Required Signature kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.signature.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                if (field.type === 'captcha' && field.captcha?.isRequired) {
                    const value = this.formValues[field._id];
                    const isEmpty = !value || !value.captchaInput || !value.isValid;
                    console.log(`🔍 Required Captcha kontrolü:`, { 
                        fieldId: field._id, 
                        fieldLabel: field.captcha.fieldLabel,
                        value, 
                        isEmpty 
                    });
                    return isEmpty;
                }
                return false;
            });

            const hasErrors = visibleFieldErrors.some((error) => error) || requiredFieldsEmpty.length > 0;
            
            console.log('🔍 Validation kontrol:', {
                visibleFieldIds,
                validationErrors: this.validationErrors,
                visibleFieldErrors,
                requiredFieldsEmpty: requiredFieldsEmpty.map(f => ({ 
                    id: f._id, 
                    type: f.type,
                    label: f.type === 'fullName' ? f.fullName?.fieldLabel : 
                           f.type === 'email' ? f.email?.fieldEmail :
                           f.type === 'address' ? f.address?.fieldAddress :
                           f.type === 'phone' ? f.phone?.fieldPhone :
                           f.type === 'datePicker' ? f.datePicker?.fieldLabel :
                           f.type === 'shortText' ? f.shortText?.fieldLabel :
                           f.type === 'longText' ? f.longText?.fieldLabel :
                           f.type === 'number' ? f.number?.fieldLabel :
                           f.type === 'hour' ? f.hour?.fieldLabel :
                           f.type === 'spinner' ? f.spinner?.fieldLabel :
                           f.type === 'dropDownList' ? f.dropDownList?.fieldLabel :
                           f.type === 'singleChoice' ? f.singleChoice?.fieldLabel :
                           f.type === 'multipleSelection' ? f.multipleSelection?.fieldLabel :
                           f.type === 'fileUpload' ? f.fileUpload?.fieldLabelFile :
                           f.type === 'signature' ? f.signature?.fieldLabel :
                           f.type === 'captcha' ? f.captcha?.fieldLabel :
                           'Unknown'
                })),
                hasErrors,
                showError: this.showError
            });
            
            this.showError = hasErrors;
            if (hasErrors) {
                console.log("❌ Form gönderimi iptal edildi: Görünür field'larda validation hataları var");
                
                // Tüm field'lar için validation durumunu güncelle
                this.sortedFields.forEach(field => {
                    if (field.type === 'fullName' && field.fullName?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.firstName || !value.lastName || 
                                       value.firstName.trim() === '' || value.lastName.trim() === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'email' && field.email?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.email || value.email.trim() === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'address' && field.address?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.addressLine1 || !value.addressLine2 || 
                                       !value.city || !value.state || !value.postalCode ||
                                       value.addressLine1.trim() === '' || value.addressLine2.trim() === '' ||
                                       value.city.trim() === '' || value.state.trim() === '' || 
                                       value.postalCode.trim() === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'phone' && field.phone?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.phoneNumber || value.phoneNumber.trim() === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'datePicker' && field.datePicker?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.date || value.date.trim() === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'shortText' && field.shortText?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = this.isSafeEmpty(value, 'shortText');
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'longText' && field.longText?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = this.isSafeEmpty(value, 'longText');
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'number' && field.number?.isRequired) {
                        const value = this.formValues[field._id];
                        const numberValue = value?.number;
                        const isEmpty = numberValue === null || numberValue === undefined || numberValue === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                        console.log(`🔍 Submit Number validation:`, {
                            fieldId: field._id,
                            value: value,
                            numberValue: numberValue,
                            isEmpty: isEmpty,
                            validationSet: isEmpty
                        });
                    }
                    if (field.type === 'dropDownList' && field.dropDownList?.isRequired) {
                        const value = this.formValues[field._id];
                        const selectedValue = value?.selected;
                        const isEmpty = !selectedValue || 
                                       (typeof selectedValue === 'string' && selectedValue.trim() === '') ||
                                       (Array.isArray(selectedValue) && (selectedValue.length === 0 || !selectedValue[0]));
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'singleChoice' && field.singleChoice?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || value === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'multipleSelection' && field.multipleSelection?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !Array.isArray(value) || value.length === 0;
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'fileUpload' && field.fileUpload?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.values || !Array.isArray(value.values) || value.values.length === 0;
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'hour' && field.hour?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.time || value.time.trim() === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                        console.log(`🔍 Submit Hour validation:`, {
                            fieldId: field._id,
                            value: value,
                            timeValue: value?.time,
                            isEmpty: isEmpty,
                            validationSet: isEmpty
                        });
                    }
                    if (field.type === 'spinner' && field.spinner?.isRequired) {
                        const value = this.formValues[field._id];
                        const spinnerValue = value?.spinnerValue;
                        const isEmpty = spinnerValue === null || spinnerValue === undefined || spinnerValue === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                        console.log(`🔍 Submit Spinner validation:`, {
                            fieldId: field._id,
                            value: value,
                            spinnerValue: spinnerValue,
                            spinnerValueType: typeof spinnerValue,
                            isEmpty: isEmpty,
                            validationSet: isEmpty
                        });
                    }
                    if (field.type === 'signature' && field.signature?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.value || value.value.trim() === '';
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                    if (field.type === 'captcha' && field.captcha?.isRequired) {
                        const value = this.formValues[field._id];
                        const isEmpty = !value || !value.captchaInput || !value.isValid;
                        this.$set(this.validationErrors, field._id, isEmpty); // ← Her zaman set et (true/false)
                    }
                });
                
                return;
            }

            console.log('✅ Form gönderimi başlıyor...');
            this.submissionTriggered = true;

            const isSpecial = this.$route.query.special === "true";
            if (isSpecial) {
                const lastSubmitTime = localStorage.getItem("lastSubmitTime");
                const currentTime = Date.now();
                const waitTime = 300000;

                if (lastSubmitTime) {
                    const timeElapsed = currentTime - lastSubmitTime;
                    if (timeElapsed < waitTime) {
                        const remainingTime = Math.ceil((waitTime - timeElapsed) / 1000 / 60);
                        alert(`Lütfen ${remainingTime} dakika bekleyin.`);
                        return;
                    }
                }

                localStorage.setItem("userDeviceId", this.userDeviceId);
            }

            const fieldsToSubmit = this.formData.formFields.filter(field => {
                if (field.type === "send") {
                    return false;
                }

                if (field.visibility !== false) {
                    return true;
                }

                console.log(`✅ Gizli field dahil edildi: ${field.label} (visibility: false)`);
                return true;
            }).sort((a, b) => a.order - b.order);

            console.log("📋 Gönderilecek field'lar:", fieldsToSubmit.map(f => ({
                label: f.label,
                type: f.type,
                slug: f.slug,
                visibility: f.visibility,
                hasValue: !!this.formValues[f._id],
                isHidden: f.visibility === false,
            })));

            const formData = {
                visitorId: this.formValues.visitorId || "",
                formId: this.formId,
                formVersionNumber: 1,
                answers: fieldsToSubmit.map((field) => {
                    let value = this.formValues[field._id];

                    if (field.visibility === false && (!value || value === "")) {
                        console.log(`🔧 Gizli field için default değer oluşturuluyor: ${field.label} (${field.type})`);
                        switch (field.type) {
                            case "fullName":
                                value = { firstName: "", lastName: "" };
                                break;
                            case "email":
                                value = { email: "" };
                                break;
                            case "shortText":
                                value = { shortText: "" };
                                break;
                            case "longText":
                                value = { longText: "" };
                                break;
                            case "number":
                                value = { number: 0 };
                                break;
                            case "phone":
                                value = { phoneNumber: "" };
                                break;
                            case "address":
                                value = {
                                    fieldAddress: "",
                                    addressLine1: "",
                                    addressLine2: "",
                                    city: "",
                                    state: "",
                                    postalCode: "",
                                };
                                break;
                            case "datePicker":
                                value = { date: "" };
                                break;
                            case "hour":
                                value = { time: "", ampm: "AM" };
                                break;
                            case "spinner":
                                value = { spinnerValue: null };
                                break;
                            case "starRating":
                            case "ratingScale":
                                value = { rating: 0 };
                                break;
                            case "dropDownList":
                                value = { selected: "" };
                                break;
                            case "singleChoice":
                                value = "";
                                break;
                            case "multipleSelection":
                                value = [];
                                break;
                            case "fileUpload":
                                value = { fieldId: field._id, values: [] };
                                break;
                            default:
                                value = "";
                                break;
                        }
                    }
                    if (typeof value === "object" && value !== null) {
                        if (field.type === "fullName") {
                            value = [value.firstName, value.lastName];
                        } else if (field.type === "address") {
                            value = [value.addressLine1, value.addressLine2, value.city, value.state, value.postalCode];
                        } else if (field.type === "phone") {
                            value = [value.phoneNumber];
                        } else if (field.type === "datePicker") {
                            value = value.date;
                        } else if (field.type === "shortText") {
                            value = value.shortText;
                        } else if (field.type === "longText") {
                            value = value.longText;
                        } else if (field.type === "dropDownList") {
                            value = value.selected;
                        } else if (field.type === "singleChoice") {
                            value = value.selected;
                        } else if (field.type === "multipleSelection") {
                            value = Array.isArray(value) ? value.filter((file) => file !== "") : value ? [value] : [];
                        } else if (field.type === "number") {
                            value = value.number;
                        } else if (field.type === "hour") {
                            value = [value.time, value.ampm];
                        } else if (field.type === "spinner") {
                            value = value.spinnerValue;
                        } else if (field.type === "starRating") {
                            value = value.rating;
                        } else if (field.type === "ratingScale") {
                            value = value.rating;
                        } else if (field.type === "fileUpload") {
                            value = value.values;
                        } else if (field.type === "email") {
                            value = value.email;
                        } else if (!Array.isArray(value)) {
                            value = Object.values(value);
                        }
                    } else {
                        value = [value];
                    }
                    return {
                        formFieldId: field._id,
                        value: value,
                    };
                }),
                userDeviceId: this.userDeviceId,
            };

            if (this.queryUsername && this.queryFullName) {
                formData.answers.push({
                    formFieldId: "66bf592e53ccfaf47176212f",
                    value: {
                        username: this.queryUsername,
                        fullName: this.queryFullName,
                    },
                    userDeviceId: this.userDeviceId,
                });
            }

            await this.submitFormData({ siteId: this.siteId, formData });

            if (isSpecial) {
                localStorage.setItem("lastSubmitTime", Date.now().toString());
            }

            Object.keys(localStorage).forEach((key) => {
                if (key !== "userDeviceId") {
                    localStorage.removeItem(key);
                }
            });
        }
    },
    mounted() {
        console.log('🔴 FormContainer mounted:', {
            hasSendButton: this.hasSendButton,
            formId: this.formId,
            siteId: this.siteId,
            isSpecial: this.isSpecial,
            timestamp: new Date().toISOString()
        });

        const special = this.$route.query.special;
        if (special === "true") {
            this.isSpecial = true;
            const div = document.getElementById("container");
            if (div) {
                div.style.background = "red";
            }
        }

        const isInIframe = window !== window.top;
        console.log('🔴 iframe check:', isInIframe);
        
        if (isInIframe) {
            console.log('🟡 iframe detected, setting timeout...');
            setTimeout(() => {
                this.isFormReady = true;
                console.log('✅ Form ready for submission in iframe');
            }, 3000);
        } else {
            this.isFormReady = true;
            console.log('✅ Form ready for submission (not in iframe)');
        }
    },
    beforeDestroy() {
        // Component destroy olurken body scroll'unu geri aç
        document.body.style.overflow = '';
    }
};
</script>

<style lang="scss" scoped>
/* Loading Overlay - Tam ekran loading */
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    z-index: 999999;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(248, 250, 252, 0.98);
    backdrop-filter: blur(10px);
}

.form-wrapper {
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    padding: 0;
    margin: 0;
}

.logo-container {
    display: inline-block;
    text-align: center;
    width: 100%;
    margin-top: 10px;
    
    img {
        max-width: 100%;
        height: auto;
        object-fit: contain;
    }
}

.container {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-xl);
    box-shadow: 
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 10px 10px -5px rgba(0, 0, 0, 0.04),
        0 0 0 1px rgba(255, 255, 255, 0.1);
    padding: clamp(1.5rem, 4vw, 3rem);
    width: 100%;
    max-width: min(90vw, 750px);
    margin: clamp(1rem, 3vw, 2rem) auto;
    position: relative;
    transition: all var(--transition);
    box-sizing: border-box;
    
    /* Modern glassmorphism effect */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(255, 255, 255, 0.05) 100%
        );
        border-radius: inherit;
        pointer-events: none;
        z-index: 1;
    }
    
    /* Content above overlay */
    & > * {
        position: relative;
        z-index: 2;
    }
    
    /* Hover effect for better interactivity */
    &:hover {
        transform: translateY(-2px);
        box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.15),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }
    
    /* Focus-within for accessibility */
    &:focus-within {
        box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.15),
            0 0 0 3px rgba(99, 102, 241, 0.1),
            0 0 0 1px var(--primary);
    }
}

.special-background {
    background: transparent !important;
    backdrop-filter: none !important;
    border: none !important;
    box-shadow: none !important;
    
    &::before {
        display: none !important;
    }
    
    &:hover {
        background: transparent !important;
        transform: none !important;
        box-shadow: none !important;
    }
    
    &:focus-within {
        box-shadow: none !important;
        border: none !important;
    }
}

/* Modern responsive design */
@media (max-width: 768px) {
    .container {
        max-width: min(95vw, 100vw - 2rem);
        margin: clamp(0.75rem, 2vw, 1.5rem) auto;
        padding: clamp(1rem, 3vw, 2rem);
        border-radius: var(--radius-lg);
        box-shadow: 
            0 10px 25px -5px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05),
            0 0 0 1px rgba(255, 255, 255, 0.1);
        
        /* Reduce transform on mobile for performance */
        &:hover {
            transform: translateY(-1px);
        }
    }
}

@media (max-width: 480px) {
    .container {
        max-width: calc(100vw - 1rem);
        margin: 0.5rem auto;
        padding: clamp(0.75rem, 2vw, 1.5rem);
        border-radius: var(--radius-md);
        box-shadow: 
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        
        /* Disable hover effects on very small screens */
        &:hover {
            transform: none;
        }
    }
    
    .special-background {
        border-radius: 0 !important;
        border: none !important;
    }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
    .container {
        transition: none;
        
        &:hover {
            transform: none;
        }
        
        &::before {
            animation: none;
        }
    }
}

/* Print styles */
@media print {
    .loading-overlay {
        display: none !important;
    }
    
    .container {
        background: white !important;
        box-shadow: none !important;
        border: 1px solid #ccc !important;
        backdrop-filter: none !important;
        
        &::before {
            display: none !important;
        }
    }
}

/* Pagination Styles */
.paginated-form {
    position: relative;
    overflow: hidden;
}

.form-page-container {
    position: relative;
    //min-height: 400px;
}

.form-page {
    width: 100%;
}

/* Page Transition Animations */
.page-slide-enter-active,
.page-slide-leave-active {
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.page-slide-enter {
    opacity: 0;
    transform: translateX(30px);
}

.page-slide-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.page-slide-enter-to,
.page-slide-leave {
    opacity: 1;
    transform: translateX(0);
}

/* Regular form (no changes needed) */
.regular-form {
    /* Existing styles remain unchanged */
}
</style>